import { Component, OnInit, OnDestroy, inject, Renderer2 } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { ContentService } from '../../services/content.service';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-root',
  template: `
  <div *ngIf="appReady">
       <top-menu-component></top-menu-component>
      <div class="mainContainer" style="height:100%;">
        <router-outlet class="mainContainer"></router-outlet>
      </div>
  </div>
  <div *ngIf="!appReady" style="display: flex; justify-content: center; align-items: center; height: 100vh; font-family: Arial, sans-serif;">
    <div style="text-align: center;">
      <div style="margin-bottom: 20px;">Loading...</div>
      <div style="width: 40px; height: 40px; border: 4px solid #f3f3f3; border-top: 4px solid #3498db; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto;"></div>
    </div>
  </div>
  `,
  styles: [`
    .angular-app-root{height:100%;width:100%;}
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `],
  standalone: false
})
export class AppRootComponent implements OnInit, OnDestroy {
    appReady = false;
    private contentService = inject(ContentService);
    private router = inject(Router);
    private renderer = inject(Renderer2);
    private routerSubscription?: Subscription;
    private currentRouteClass?: string;

    ngOnInit(): void {
        // Wait for content to be ready before showing the app
        this.contentService.initAll().then(() => {
            this.appReady = true;
            // Start tracking route changes after app is ready
            this.setupRouteClassTracking();
        }).catch(err => {
            console.error('Failed to initialize content:', err);
            // Show app anyway to prevent infinite loading
            this.appReady = true;
            this.setupRouteClassTracking();
        });
    }

    ngOnDestroy(): void {
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe();
        }
        // Clean up current route class on destroy
        if (this.currentRouteClass) {
            this.renderer.removeClass(document.body, this.currentRouteClass);
        }
    }

    private setupRouteClassTracking(): void {
        // Listen to router events and update body class
        this.routerSubscription = this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                this.updateBodyClass(event.url);
            });

        // Set initial class for current route
        this.updateBodyClass(this.router.url);
    }

    private updateBodyClass(url: string): void {
        // Remove previous route class
        if (this.currentRouteClass) {
            this.renderer.removeClass(document.body, this.currentRouteClass);
        }

        // Generate new class name from route
        const routeClass = this.urlToClassName(url);
        
        if (routeClass) {
            this.renderer.addClass(document.body, routeClass);
            this.currentRouteClass = routeClass;
        }
    }

    private urlToClassName(url: string): string {
        // Extract the main route path (first segment after /)
        let routePath = url
            .split('?')[0] // Remove query parameters
            .split('#')[0] // Remove fragments
            .replace(/^\//, '') // Remove leading slash
            .split('/')[0]; // Take only the first segment (main route)

        // Handle root path
        if (!routePath) {
            routePath = 'Main';
        }

        // Add state- prefix to match your desired format
        return `state-${routePath}`;
    }
}
